// Deterministic per-column value profiling for census imports.
//
// The column matcher used to look only at header text, which is exactly how a
// TPA export full of spacer columns, "FT" hours, and unlabeled dollar columns
// got nonsense proposals (an empty column "matched" to FICA wages; "MI" matched
// to termination date). This module reads a sample of each column's VALUES and
// classifies its shape, so the matcher can skip empty columns, veto guesses the
// data contradicts, and describe unlabeled-but-populated columns to the user.
// Everything here is local and token-free; nothing leaves the browser.

const SAMPLE_LIMIT = 80

const isBlank = (value) => value === null || value === undefined || String(value).trim() === ''

const DATE_RE = /^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$|^\d{4}-\d{1,2}-\d{1,2}$|^[A-Za-z]{3,9}\.? \d{1,2},? \d{4}$/
const MONEY_RE = /^\(?\s*-?\s*\$\s*[\d,]+(\.\d+)?\s*\)?$|^\(?-?[\d,]{4,}\.\d{2}\)?$/
const NUMBER_RE = /^-?[\d,]*\.?\d+$/
const PERCENT_RE = /^-?[\d.]+\s*%$/
const SSN_RE = /^\d{3}-\d{2}-\d{4}$|^\d{9}$/

const YN_SET = new Set(['y', 'n', 'yes', 'no', 'true', 'false'])
const GENDER_SET = new Set(['m', 'f', 'u', 'male', 'female'])
const FTPT_SET = new Set(['ft', 'pt', 'f/t', 'p/t', 'full', 'part', 'fulltime', 'parttime', 'full-time', 'part-time', 'full time', 'part time'])
const STATUS_SET = new Set(['active', 'terminated', 'term', 'termed', 'inactive', 'leave', 'loa', 'separated', 'employed'])

// Classify one trimmed cell. Order matters: "$1,234.56" must be money before
// number, "6%" percent before number, and an SSN before a plain number.
export function classifyValue(raw) {
  const text = String(raw).trim()
  const lower = text.toLowerCase()
  if (DATE_RE.test(text)) return 'date'
  if (PERCENT_RE.test(text)) return 'percent'
  if (SSN_RE.test(text)) return 'ssn'
  if (MONEY_RE.test(text)) return 'money'
  if (NUMBER_RE.test(text)) return 'number'
  if (FTPT_SET.has(lower)) return 'ftpt'
  if (GENDER_SET.has(lower)) return 'gender'
  if (YN_SET.has(lower)) return 'yn'
  if (STATUS_SET.has(lower)) return 'status'
  return 'text'
}

// Profile every column of a raw string matrix. `shape` is the dominant value
// class when it covers >= 60% of non-blank cells, else 'mixed'; a column with
// no non-blank cells is 'empty'. `samples` keeps the first few distinct raw
// values for UI hints ("$3,080.61, $1,909.02, ...").
export function profileColumns(rows, columnCount) {
  const profiles = Array.from({ length: columnCount }, (_, index) => ({
    index,
    nonBlank: 0,
    counts: {},
    samples: [],
    numbers: [],
  }))
  let sampled = 0
  for (const cells of rows || []) {
    if (sampled >= SAMPLE_LIMIT) break
    sampled += 1
    for (let index = 0; index < columnCount; index += 1) {
      const value = cells?.[index]
      if (isBlank(value)) continue
      const profile = profiles[index]
      profile.nonBlank += 1
      const kind = classifyValue(value)
      profile.counts[kind] = (profile.counts[kind] || 0) + 1
      const text = String(value).trim()
      if (kind === 'number') profile.numbers.push(Number(text.replace(/,/g, '')))
      if (profile.samples.length < 3 && !profile.samples.includes(text)) profile.samples.push(text)
    }
  }
  for (const profile of profiles) {
    if (profile.nonBlank === 0) {
      profile.shape = 'empty'
      continue
    }
    const [topKind, topCount] = Object.entries(profile.counts).sort((a, b) => b[1] - a[1])[0]
    profile.shape = topCount >= profile.nonBlank * 0.6 ? topKind : 'mixed'
    // A counter column (1, 2, 3, …) is spreadsheet furniture, not data; flag
    // it so the matcher can skip it instead of asking the user about it. Real
    // files delete and append rows, so tolerate gaps (1…18, 20…26 still
    // counts) – the tell is dense, strictly increasing integers starting at 1.
    const numbers = profile.numbers
    profile.sequentialFromOne = profile.shape === 'number'
      && numbers.length >= 3
      && numbers[0] === 1
      && numbers.every((value, index) => Number.isInteger(value) && (index === 0 || value > numbers[index - 1]))
      && numbers[numbers.length - 1] <= numbers.length + 5
  }
  return profiles
}

// Which value shapes are plausible for each census field family. A column whose
// dominant shape is NOT in the field's set contradicts the guess and vetoes it
// ('mixed' never vetoes – messy columns stay eligible, just low-confidence).
const DATE_FIELDS = ['dob', 'hire_date', 'termination_date', 'cb_participant_entry_date']
const MONEY_FIELDS = ['compensation', 'prior_year_compensation', 'high_3_compensation', 'section_415_compensation', 'pretax_amount', 'roth_amount', 'after_tax_amount', 'balance', 'loan_balance', 'forfeiture_allocation', 'qnec_amount']
const PERCENT_FIELDS = ['pretax_percent', 'roth_percent', 'after_tax_percent', 'ownership_percent']
const FLAG_FIELDS = ['key_employee', 'five_percent_owner']
const TEXT_FIELDS = ['name', 'first_name', 'last_name', 'family_of', 'allocation_group']

const FIELD_SHAPES = (() => {
  const map = {}
  for (const field of DATE_FIELDS) map[field] = new Set(['date'])
  for (const field of MONEY_FIELDS) map[field] = new Set(['money', 'number'])
  for (const field of PERCENT_FIELDS) map[field] = new Set(['percent', 'number'])
  for (const field of FLAG_FIELDS) map[field] = new Set(['yn', 'number', 'text'])
  for (const field of TEXT_FIELDS) map[field] = new Set(['text', 'ssn', 'number'])
  map.id = new Set(['text', 'ssn', 'number'])
  map.gender = new Set(['gender', 'yn', 'text'])
  map.status = new Set(['status', 'text'])
  map.full_time = new Set(['ftpt', 'yn', 'text'])
  map.hours_served = new Set(['number', 'ftpt'])
  map.tenure = new Set(['number', 'text'])
  return map
})()

// Whether a column shaped like `shape` can plausibly hold `field`. Unknown
// fields and messy ('mixed') columns are always allowed – vetoes are reserved
// for clear contradictions like a text column claiming a money field.
export function shapeAllowsField(shape, field) {
  if (!shape || shape === 'mixed') return true
  const allowed = FIELD_SHAPES[field]
  if (!allowed) return true
  return allowed.has(shape)
}

// Fields worth OFFERING for an unlabeled column of a given shape, most likely
// first. Used for "Column 22 has dollar amounts – import it as …" suggestions;
// never auto-assigned.
export function fieldsForShape(shape) {
  switch (shape) {
    case 'date': return [...DATE_FIELDS]
    case 'money': return [...MONEY_FIELDS]
    case 'percent': return [...PERCENT_FIELDS]
    case 'ftpt': return ['full_time']
    case 'gender': return ['gender']
    case 'yn': return ['full_time', 'key_employee', 'five_percent_owner']
    case 'status': return ['status']
    case 'ssn': return ['id']
    default: return []
  }
}
